import { useSnackbar } from 'notistack';
import { useRef, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TableContainer from '@mui/material/TableContainer';
import LinearProgress from '@mui/material/LinearProgress';

import { useBoolean } from 'src/hooks/use-boolean';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import candidate_scan from 'src/assets/lottie/candidate.json';
import { generateUploadError } from 'src/services/files/file.utils';
import { useUploadFileMutation } from 'src/services/files/file.service';
import {
  CandidateImportJob,
  CandidateImportJobStatus,
} from 'src/services/candidates/candidates.types';
import {
  useLazyGetCandidateImportJobQuery,
  useCreateCandidateImportJobMutation,
} from 'src/services/candidates/candidates.service';

import Iconify from 'src/components/iconify';
import { Upload } from 'src/components/upload';
import { useSettingsContext } from 'src/components/settings';
import AnimationContainer from 'src/components/animation/container';

type Props = {
  organizationId: string;
  open: boolean;
  onClose: VoidFunction;
  createInvite?: boolean;
};

const MAX_FILES = 15;
const POLL_INTERVAL = 2000; // 2 seconds

export default function CandidateFileImporter({ organizationId, open, onClose, createInvite = false }: Props) {
  const { t } = useTranslate();
  const settings = useSettingsContext();
  const { enqueueSnackbar } = useSnackbar();
  const authContext = useAuthContext();

  const minimized = useBoolean();
  const [files, setFiles] = useState<File[]>([]);
  const [uploadedFileIds, setUploadedFileIds] = useState<string[]>([]);
  const [importJob, setImportJob] = useState<CandidateImportJob | null>(null);
  const [uploadingIndex, setUploadingIndex] = useState<number>(-1);

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [pollingFailedCount, setPollingFailedCount] = useState<number>(0);

  const [uploadFile] = useUploadFileMutation();
  const [createImportJob, { isLoading: creatingJob }] = useCreateCandidateImportJobMutation();
  const [getImportJob] = useLazyGetCandidateImportJobQuery();

  const isUploading = uploadingIndex >= 0;
  const isProcessing = importJob && [CandidateImportJobStatus.PENDING, CandidateImportJobStatus.IN_PROGRESS].includes(importJob.status);
  const isCompleted = importJob?.status === CandidateImportJobStatus.COMPLETED;
  const isFailed = importJob?.status === CandidateImportJobStatus.FAILED;

  // Poll for job status
  const pollJobStatus = useCallback(async (jobId: string) => {
    try {
      const result = await getImportJob(jobId).unwrap();
      setImportJob(result);

      if (result.status === CandidateImportJobStatus.COMPLETED || result.status === CandidateImportJobStatus.FAILED) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }

        if (result.status === CandidateImportJobStatus.COMPLETED) {
          enqueueSnackbar(t('components.candidate_file_importer.success'), { variant: 'success' });
        } else {
          enqueueSnackbar(t('components.candidate_file_importer.failed'), { variant: 'error' });
        }
      }
    } catch (error) {
      console.error('Error polling job status:', error);
      setPollingFailedCount((count) => count + 1);
      if (pollIntervalRef.current && pollingFailedCount >= 5) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    }
  }, [getImportJob, enqueueSnackbar, t, pollingFailedCount]);

  // Start polling when job is created
  useEffect(() => {
    if (importJob?.id && isProcessing) {
      pollIntervalRef.current = setInterval(() => {
        pollJobStatus(importJob.id);
      }, POLL_INTERVAL);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [importJob?.id, isProcessing, pollJobStatus]);

  const handleFileDrop = useCallback(
    (acceptedFiles: File[]) => {
      const totalFiles = files.length + acceptedFiles.length;

      if (totalFiles > MAX_FILES) {
        enqueueSnackbar(`Maximum ${MAX_FILES} files allowed`, { variant: 'warning' });
        return;
      }

      setFiles((prev) => [...prev, ...acceptedFiles]);
    },
    [files.length, enqueueSnackbar]
  );

  const handleRemoveFile = useCallback((file: File | string) => {
    setFiles((prev) => prev.filter((f) => f !== file));
  }, []);

  const handleRemoveAllFiles = useCallback(() => {
    setFiles([]);
  }, []);

  const handleUploadFiles = useCallback(async () => {
    try {
      const fileIds: string[] = [];

      // Upload each file sequentially
      for (let i = 0; i < files.length; i++) {
        setUploadingIndex(i);

        // eslint-disable-next-line no-await-in-loop
        const uploadedFile = await uploadFile({
          file: files[i],
          type: 'document' as any,
        }).unwrap();
        fileIds.push(uploadedFile.id);
      }

      setUploadingIndex(-1);
      setUploadedFileIds(fileIds);

      // Create import job
      const job = await createImportJob({
        organization_id: organizationId,
        file_ids: fileIds,
        create_invite: createInvite,
      }).unwrap();

      setImportJob(job);
    } catch (error) {
      console.error('Error uploading files:', error);
      const errorMsg = generateUploadError(error);
      enqueueSnackbar(errorMsg, { variant: 'error' });
      setUploadingIndex(-1);
    }
  }, [files, uploadFile, createImportJob, organizationId, createInvite, enqueueSnackbar]);

  const handleClose = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setFiles([]);
    setUploadedFileIds([]);
    setImportJob(null);
    setUploadingIndex(-1);
    onClose();
  }, [onClose]);

  const handleViewCandidate = useCallback((candidateId: string) => {
    window.open(`/dashboard/candidates/${candidateId}`, '_blank');
  }, []);

  const renderUploadSection = (
    <Box width="100%" maxHeight="60vh">
      {!isUploading && !isProcessing && !isCompleted ? (
        <Upload
          multiple
          accept={{ '.pdf': [], '.docx': [], '.doc': [] }}
          files={files}
          onDrop={handleFileDrop}
          onRemove={handleRemoveFile}
          onRemoveAll={handleRemoveAllFiles}
          sx={{ mt: 1 }}
          helperText={
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('components.candidate_file_importer.upload_instruction', { max_files: MAX_FILES })}
            </Typography>
          }
        />
      ) : (
        <Box display="flex" flexDirection="column" justifyContent="center" textAlign="center" alignItems="center">
          {(isProcessing || isUploading) && <AnimationContainer height={250} width={320} json={candidate_scan} loop />}
          {isUploading && <Typography
            fontSize={13}
            className={settings.themeMode === 'light' ? 'text-gray-600' : 'text-gray-100'}
            sx={{ mt: 2 }}
          >
            {`Uploading files... (${uploadingIndex + 1}/${files.length})`}
          </Typography>
          }

          {isProcessing && <Typography
            fontSize={13}
            className={settings.themeMode === 'light' ? 'text-gray-600' : 'text-gray-100'}
            sx={{ mt: 2 }}
          >
            Processing resumes...
          </Typography>
          }

          {isProcessing && <LinearProgress sx={{ width: '100%', mt: 2 }} />}

          {/* Show live counts during processing */}
          {isProcessing && importJob?.results && (
            <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
              <Card sx={{ px: 2, py: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('common.processed')}
                </Typography>
                <Typography variant="h6">{importJob.results.processed_count}/{importJob.results.total_count}</Typography>
              </Card>
              <Card sx={{ px: 2, py: 1, bgcolor: 'success.lighter' }}>
                <Typography variant="caption" color="text.secondary">
                  {t('common.success')}
                </Typography>
                <Typography variant="h6" color="success.main">{importJob.results.success_count}</Typography>
              </Card>
              <Card sx={{ px: 2, py: 1, bgcolor: 'error.lighter' }}>
                <Typography variant="caption" color="text.secondary">
                  {t('common.failed')}
                </Typography>
                <Typography variant="h6" color="error.main">{importJob.results.failed_count}</Typography>
              </Card>
            </Stack>
          )}
        </Box>
      )}
    </Box>
  );

  const renderResultsSection = isCompleted && importJob?.results && (
    <Box sx={{ mt: 3 }}>
      {/* Summary Cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Card sx={{ flex: 1, p: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {t('common.total')}
          </Typography>
          <Typography variant="h4">{importJob.results.total_count}</Typography>
        </Card>
        <Card sx={{ flex: 1, p: 2, bgcolor: 'success.lighter' }}>
          <Typography variant="caption" color="text.secondary">
            {t('common.success')}
          </Typography>
          <Typography variant="h4" color="success.main">
            {importJob.results.success_count}
          </Typography>
        </Card>
        <Card sx={{ flex: 1, p: 2, bgcolor: 'error.lighter' }}>
          <Typography variant="caption" color="text.secondary">
            {t('common.failed')}
          </Typography>
          <Typography variant="h4" color="error.main">
            {importJob.results.failed_count}
          </Typography>
        </Card>
      </Stack>

      {/* Results Table */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('common.email')}</TableCell>
              <TableCell>{t('common.first_name')}</TableCell>
              <TableCell>{t('common.last_name')}</TableCell>
              <TableCell align="right">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {importJob.results.import_results.map((result, index) => (
              <TableRow key={index}>
                <TableCell>{result.email}</TableCell>
                <TableCell>{result.first_name}</TableCell>
                <TableCell>{result.last_name}</TableCell>
                <TableCell align="right">
                  {result.candidate_id ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleViewCandidate(result.candidate_id)}
                      startIcon={<Iconify icon="mdi:open-in-new" />}
                    >
                      {t('common.view')}
                    </Button>
                  ) : (
                    <Chip label="Failed" size="small" color="error" />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  // Minimized view
  if (minimized.value) {
    return (
      <Box
        sx={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 1300,
          width: 360,
        }}
      >
        <Card sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2">{t('components.candidate_file_importer.minimized_title')}</Typography>

              {isProcessing && <Typography variant="caption" color="text.secondary">
                {`Processing: ${importJob?.results.processed_count || 0}/${importJob?.results.total_count || 0}`}
              </Typography>
              }

              {isCompleted && <Typography variant="caption" color="text.secondary">
                {`${importJob?.results.success_count}/${importJob?.results.total_count} imported`}
              </Typography>
              }
            </Box>
            <IconButton size="small" onClick={minimized.onFalse}>
              <Iconify icon="mdi:window-maximize" />
            </IconButton>
            <IconButton size="small" onClick={handleClose}>
              <Iconify icon="mdi:close" />
            </IconButton>
          </Stack>
          {isProcessing && <LinearProgress sx={{ mt: 1 }} />}

          {/* Show live counts in minimized view */}
          {isProcessing && importJob?.results && (
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <Chip
                label={`Success: ${importJob.results.success_count}`}
                size="small"
                color="success"
                variant="outlined"
              />
              <Chip
                label={`Failed: ${importJob.results.failed_count}`}
                size="small"
                color="error"
                variant="outlined"
              />
            </Stack>
          )}
        </Card>
      </Box>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={isProcessing ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '500px' },
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{t('components.candidate_file_importer.title')}</Typography>
          <Stack direction="row" spacing={1}>
            <IconButton
              size="small"
              onClick={minimized.onTrue}
              disabled={!!(!isProcessing && !isCompleted)}
            >
              <Iconify icon="mdi:window-minimize" />
            </IconButton>
            <IconButton
              size="small"
              onClick={handleClose}
              disabled={!!isProcessing}
            >
              <Iconify icon="mdi:close" />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {renderUploadSection}
        {renderResultsSection}
      </DialogContent>

      <DialogActions>
        {!isCompleted && !isProcessing && (
          <>
            <Button onClick={handleClose} disabled={isUploading}>
              {t('common.cancel')}
            </Button>
            <LoadingButton
              variant="contained"
              onClick={handleUploadFiles}
              loading={isUploading || creatingJob}
              disabled={files.length === 0}
            >
              {t('components.candidate_file_importer.upload', { file_count: files.length })}
            </LoadingButton>
          </>
        )}
        {isCompleted && (
          <Button variant="contained" onClick={handleClose}>
            {t('common.done')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}